import { useCallback, useEffect, useState } from 'react'
import { AlertTriangle, FileCheck2, Landmark, ShieldCheck } from 'lucide-react'
import { useI18n } from '../i18n'
import { useStore } from '../data/store'
import { supabase } from '../lib/supabase'
import { addDays } from '../lib/dates'
import { assertWrote } from '../data/supabaseAdapter'
import { Button, Card, Field, Input, cx } from '../ui/kit'
import RksvGuide from './RksvGuide'
import { QrCode } from '../ui/QrCode'
import { darf, werDarf } from '../lib/rollen'
import { useAuth } from '../auth'
import { kaufwegSichtbar } from '../lib/kaufweg'
import { formatieren } from '../lib/steuernummer'

/**
 * The two Austrian obligations a landlord cannot opt out of, and the honest state of
 * each: the Ortstaxe declaration to the Gemeinde, and RKSV.
 */

/* ------------------------------------------------------------- Ortstaxe */

interface Filing {
  id: string
  period_start: string
  period_end: string
  municipality: string
  liable_person_nights: number
  amount: number
  status: string
  reference: string | null
  submitted_at: string | null
}

/** First of last month — the period a landlord declares at the start of this one. */
function lastMonth(): { from: string; to: string } {
  const now = new Date()
  const first = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1))
  const next = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))
  return { from: first.toISOString().slice(0, 10), to: next.toISOString().slice(0, 10) }
}

/**
 * What the demo cannot show. Ortstaxe filings, RKSV and Stripe all need a real
 * tenant behind them, so in the demo their cards render nothing — which looks
 * exactly like a product that does not have them.
 */
/**
 * A real RKSV payload shape, with obviously fake values. Shown in the demo so the
 * module can be demonstrated rather than described — and marked as unsigned, because
 * a sample that looked verifiable would be the one lie this module cannot afford.
 */
const DEMO_RECEIPT = '_R1-AT0_DEMO-KASSE_37_2026-07-27T09:14:22_554,50_0,00_0,00_'
  + '32,00_0,00_kJ8vQm2xNqA=_DEMO-CERT_9pQ2Lm4a'

export function ComplianceDemoNote() {
  const { lang } = useI18n()
  const store = useStore()
  const de = lang === 'de'
  if (store.live) return null

  return (
    <Card className="p-4 space-y-2">
      <h2 className="font-medium flex items-center gap-2">
        <ShieldCheck size={18} /> {de ? 'Nur im echten Konto' : 'Real accounts only'}
      </h2>
      <p className="text-sm text-muted">
        {de
          ? 'Diese Funktionen brauchen einen echten Betrieb und sind in der Demo deshalb ausgeblendet:'
          : 'These need a real business behind them and are therefore hidden in the demo:'}
      </p>
      <div className="rounded-xl bg-surface-2 p-3 flex gap-3 items-center">
        <QrCode value={DEMO_RECEIPT} size={96} />
        <div className="text-xs text-muted space-y-1">
          <p className="font-medium text-fg">
            {de ? 'So sieht ein signierter Beleg aus' : 'What a signed receipt looks like'}
          </p>
          <p>
            {de
              ? 'Maschinenlesbarer Code nach RKSV: Kasse, Belegnummer, Beträge je Steuersatz, verschlüsselter Umsatzzähler und die Signatur des vorigen Belegs.'
              : 'The RKSV machine-readable code: register, receipt number, amounts per VAT rate, the encrypted turnover counter and the previous receipt’s signature.'}
          </p>
          <p className="opacity-70">
            {de
              ? 'Beispielbeleg — nicht signiert, nicht prüfbar.'
              : 'Sample receipt — unsigned, not verifiable.'}
          </p>
        </div>
      </div>

      <ul className="text-sm space-y-1.5">
        <li>
          <span className="font-medium">{de ? 'Ortstaxe-Meldung' : 'Tourist tax filing'}</span>
          {' — '}
          {de
            ? 'Monat abschließen, Zahlen einfrieren, CSV für die Gemeinde.'
            : 'close a month, freeze the figures, CSV for the municipality.'}
        </li>
        <li>
          <span className="font-medium">{de ? 'Registrierkasse (RKSV)' : 'Cash register (RKSV)'}</span>
          {' — '}
          {de
            ? 'Belegkette, Datenerfassungsprotokoll, Anleitung Schritt für Schritt, und eine Warnung bevor die Pflicht eintritt.'
            : 'receipt chain, audit log, a step-by-step guide, and a warning before the obligation starts.'}
        </li>
        <li>
          <span className="font-medium">{de ? 'Gästezahlungen' : 'Guest payments'}</span>
          {' — '}
          {de
            ? 'Stripe auf Ihr eigenes Konto, das Geld läuft nie über uns.'
            : 'Stripe into your own account; the money never passes through us.'}
        </li>
      </ul>
    </Card>
  )
}

export function TouristTaxFilingCard() {
  // Die Abgabenmeldung ist seit Vorgang 013 owner/manager. Der Knopf bleibt
  // SICHTBAR und wird gesperrt: Ein Mitarbeiter soll sehen, dass es die Meldung
  // gibt, und wissen, wen er fragen muss — ein verschwundener Knopf sieht aus
  // wie eine fehlende Funktion.
  const { rolle } = useAuth()
  const darfMelden = darf(rolle, 'meldung')
  const { lang, formatDate, formatMoney } = useI18n()
  const store = useStore()
  const de = lang === 'de'

  const [{ from, to }, setPeriod] = useState(lastMonth)
  const [preview, setPreview] = useState<{ nights: number; amount: number } | null>(null)
  const [filings, setFilings] = useState<Filing[]>([])
  const [reference, setReference] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!store.live || !supabase) return
    const tenant = store.db.tenant.id
    const [{ data: rep }, { data: rows }] = await Promise.all([
      supabase.rpc('tourist_tax_report', { p_tenant: tenant, p_from: from, p_to: to }),
      supabase.from('tourist_tax_filings').select('*')
        .order('period_start', { ascending: false }).limit(12),
    ])
    const list = (rep ?? []) as { liable_person_nights: number; amount: number }[]
    setPreview({
      nights: list.reduce((a, r) => a + Number(r.liable_person_nights ?? 0), 0),
      amount: list.reduce((a, r) => a + Number(r.amount ?? 0), 0),
    })
    setFilings((rows ?? []) as Filing[])
  }, [store.live, store.db.tenant.id, from, to])

  useEffect(() => { void load() }, [load])

  if (!store.live) return null

  const alreadyFiled = filings.some(
    (f) => f.period_start === from && f.status === 'submitted')

  const submit = async () => {
    if (!supabase || !preview) return

    // Eine Meldung ist unwiderruflich — sie friert die Zahlen ein und lässt sich
    // danach nur noch stornieren, nicht korrigieren. Ein Klick ohne Rückfrage war
    // zu wenig: der Knopf stand direkt neben der Zeitraumauswahl.
    const zeitraum = `${formatDate(from)} – ${formatDate(addDays(to, -1))}`
    const frage = de
      ? `Meldung für ${zeitraum} jetzt abgeben?\n\n`
        + `Pflichtige Nächtigungen: ${preview.nights}\n`
        + `Betrag: ${formatMoney(preview.amount)}\n\n`
        + 'Die Zahlen werden eingefroren. Spätere Änderungen an Buchungen ändern die '
        + 'Meldung nicht mehr.'
      : `File the declaration for ${zeitraum}?\n\n`
        + `Liable person-nights: ${preview.nights}\n`
        + `Amount: ${formatMoney(preview.amount)}\n\n`
        + 'The figures are frozen. Later changes to bookings will not alter it.'
    if (!window.confirm(frage)) return

    // Null Nächtigungen sind fast immer der falsche Zeitraum, nicht ein leerer Monat.
    if (preview.nights === 0 && !window.confirm(de
      ? 'Für diesen Zeitraum gibt es keine pflichtigen Nächtigungen. Trotzdem eine '
        + 'Nullmeldung abgeben?'
      : 'There are no liable person-nights in this period. File a nil return anyway?')) {
      return
    }

    setBusy(true); setError(null)
    const { error: e } = await supabase.rpc('submit_tourist_tax_filing', {
      p_tenant: store.db.tenant.id, p_from: from, p_to: to,
      p_property: null, p_reference: reference || null, p_note: null,
    })
    if (e) setError(e.message)
    else { setReference(''); await load() }
    setBusy(false)
  }

  /** CSV, because that is what a Gemeinde or a Tourismusverband actually accepts. */
  const exportCsv = () => {
    const head = ['Zeitraum von', 'Zeitraum bis', 'Gemeinde', 'Naechtigungen', 'Betrag', 'Status', 'Referenz']
    const rows = filings.map((f) => [
      f.period_start, f.period_end, f.municipality,
      String(f.liable_person_nights), String(f.amount), f.status, f.reference ?? '',
    ])
    const csv = [head, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';')).join('\r\n')
    const url = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }))
    const a = document.createElement('a')
    a.href = url
    a.download = `ortstaxe-${store.db.tenant.slug || 'meldung'}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <Card className="p-4 space-y-3">
      <h2 className="font-medium flex items-center gap-2">
        <Landmark size={18} /> {de ? 'Ortstaxe-Meldung' : 'Tourist tax declaration'}
      </h2>
      <p className="text-sm text-muted">
        {de
          ? 'Die Meldung friert die Zahlen des Zeitraums ein. Eine spätere Korrektur an einer Buchung ändert nicht mehr, was der Gemeinde gemeldet wurde.'
          : 'Filing freezes the figures for the period. A later correction to a booking cannot change what the municipality was told.'}
      </p>

      <div className="grid grid-cols-2 gap-3">
        <Field label={de ? 'Von' : 'From'}>
          <Input type="date" value={from}
            onChange={(e) => setPeriod({ from: e.target.value, to })} />
        </Field>
        <Field label={de ? 'Bis (exklusive)' : 'To (exclusive)'}>
          <Input type="date" value={to}
            onChange={(e) => setPeriod({ from, to: e.target.value })} />
        </Field>
      </div>

      {preview && (
        <div className="rounded-xl bg-surface-2 px-3 py-2 text-sm flex items-center gap-4">
          <span>
            <span className="text-muted">{de ? 'Pflichtige Nächtigungen' : 'Liable person-nights'}: </span>
            <span className="font-medium tabular-nums">{preview.nights}</span>
          </span>
          <span className="ml-auto font-medium tabular-nums">{formatMoney(preview.amount)}</span>
        </div>
      )}

      <Field label={de ? 'Aktenzeichen der Gemeinde (optional)' : 'Municipality reference (optional)'}>
        <Input value={reference} onChange={(e) => setReference(e.target.value)} />
      </Field>

      {error && <p className="text-sm bg-danger-soft text-danger rounded-xl px-3 py-2">{error}</p>}

      <div className="flex gap-2 flex-wrap">
        <Button variant="primary" disabled={busy || alreadyFiled || !preview || !darfMelden} onClick={submit}>
          <FileCheck2 size={16} />
          {alreadyFiled
            ? (de ? 'Bereits gemeldet' : 'Already filed')
            : (de ? 'Zeitraum melden' : 'File this period')}
        </Button>
        {filings.length > 0 && (
          <Button variant="secondary" onClick={exportCsv}>CSV</Button>
        )}
      </div>

      {!darfMelden && (
        <p className="text-xs text-muted">{werDarf('meldung')}</p>
      )}

      {filings.length > 0 && (
        <ul className="text-sm divide-y divide-border">
          {filings.map((f) => (
            <li key={f.id} className="py-2 flex items-center gap-3">
              <span className="flex-1">
                {formatDate(f.period_start)} – {formatDate(addDays(f.period_end, -1))}
                {f.reference && <span className="text-muted"> · {f.reference}</span>}
                {f.status === 'cancelled' && (
                  <span className="text-muted"> · {de ? 'storniert' : 'cancelled'}</span>
                )}
              </span>
              <span className={cx('tabular-nums', f.status === 'cancelled' && 'line-through opacity-60')}>
                {f.liable_person_nights}
              </span>
              <span className={cx('tabular-nums font-medium',
                f.status === 'cancelled' && 'line-through opacity-60')}>
                {formatMoney(f.amount)}
              </span>
              {f.status === 'submitted' && (
                <button
                  className="text-xs text-danger underline underline-offset-2"
                  onClick={async () => {
                    // Die Zahlen bleiben stehen — storniert heisst zurückgezogen,
                    // nicht gelöscht. Was gemeldet wurde, bleibt nachvollziehbar.
                    if (!window.confirm(de
                      ? 'Diese Meldung als storniert kennzeichnen? Die Zahlen bleiben '
                        + 'zur Nachvollziehbarkeit erhalten.'
                      : 'Mark this declaration as cancelled? The figures are kept for the record.')) return
                    if (!supabase) return
                    // Das anschliessende `load()` holt zwar die Wahrheit zurück, aber
                    // erst nachdem der Knopf kommentarlos quittiert hat. Ohne die
                    // Zeilenzahl sähe der Vermieter die Meldung kurz als storniert und
                    // dann wieder als offen — ohne je zu erfahren, warum.
                    try {
                      assertWrote(
                        await supabase.from('tourist_tax_filings')
                          .update({ status: 'cancelled' }).eq('id', f.id).select('id'),
                        'Meldung stornieren',
                      )
                      await load()
                    } catch (err) {
                      setError((err as Error).message)
                    }
                  }}
                >
                  {de ? 'stornieren' : 'cancel'}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}

/* ----------------------------------------------------------------- RKSV */

/** What the payment history says about the two thresholds. */
export interface RksvThresholds {
  year: number
  turnover: number
  cash_like: number
  by_method: Record<string, number>
  over_turnover: boolean
  over_cash: boolean
  obligated: boolean
  crossed_on: string | null
  obliged_from: string | null
  closeness: number
}

export async function loadThresholds(tenantId: string): Promise<RksvThresholds | null> {
  if (!supabase) return null
  const { data } = await supabase.rpc('rksv_threshold_status', { p_tenant: tenantId })
  return (data as RksvThresholds | null) ?? null
}

/** Der kaufmännische Modulzustand aus `rksv_modul`, wie `status` (v19) ihn liefert. */
interface RksvModulStand {
  zustand: 'bestellt' | 'aktiv' | 'gekuendigt' | 'beendet' | 'storniert'
  aktiv_ab: string | null
  zuschlag_ab: string | null
  mindestbezug_bis: string | null
  gekuendigt_zum: string | null
}

interface RksvStatus {
  enabled: boolean
  provider: string
  credentials_set: boolean
  kassen_id: string
  startbeleg_at: string | null
  finanzonline_registered_at: string | null
  receipts: number
  compliant: boolean
  // Vorgang 018 (v19): Modulzustand, der eine gepflegte Betrag (NULL = keiner,
  // nie 0,00) und der Gate-Zustand für den ehrlichen Termin (Auflage A4).
  modul: RksvModulStand | null
  modul_preis: number | null
  produktiv: boolean
}

/** Hex-SHA-256 über den bestätigten Wortlaut — dieselbe Rechnung wie im Server. */
async function sha256Hex(text: string): Promise<string> {
  const d = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text))
  return Array.from(new Uint8Array(d)).map((b) => b.toString(16).padStart(2, '0')).join('')
}

/** Der benannte Fehlercode aus einer abgelehnten Function-Antwort, falls lesbar. */
async function rksvFehlerCode(e: unknown): Promise<string | null> {
  const ctx = (e as { context?: Response }).context
  if (ctx && typeof ctx.clone === 'function') {
    try {
      const body = await ctx.clone().json() as { error?: string }
      return body.error ?? null
    } catch { /* kein JSON — dann bleibt die generische Meldung */ }
  }
  return null
}

/**
 * Die Bestellstrecke des RKSV-Zusatzmoduls (Vorgang 018, AK3–AK9, AK13/AK14).
 *
 * Der Name ist der WÄCHTER-MARKER (Auflage A3): jede Datei, die `rksvBestell…`
 * nennt, muss `kaufwegSichtbar` kennen — `kaufweg.test.ts`, dritte Regel. In
 * der nativen Hülle zeigt diese Komponente weder Einstieg noch Preis noch
 * irgendeinen Hinweis auf einen Kaufweg außerhalb der App (Guideline 3.1.1-
 * Umfeld); der Modulzustand selbst bleibt sichtbar, denn ein bestehendes
 * Modul ist Kontoauskunft, keine Kaufaufforderung.
 *
 * Alles hier ist Komfort, kein Tor: dieselben Bedingungen (owner, AT,
 * Web-Zahler, Nummer, Bestätigung) prüft die Edge Function `rksv` (v19)
 * serverseitig noch einmal — am UI vorbei heißt nicht am Tor vorbei.
 */
function RksvBestellstrecke({ status, call, reload }: {
  status: RksvStatus
  call: (action: string, extra?: Record<string, unknown>) => Promise<Record<string, unknown>>
  reload: () => Promise<void>
}) {
  const { lang, t, formatDate, formatMoney } = useI18n()
  const store = useStore()
  const { rolle } = useAuth()
  const istOwner = rolle === 'owner'

  // Der Abrechnungskanal, für die ehrliche Meldung (F2/AK14): NULL heißt
  // Web-Zahler — dieselbe Lesart wie abo_laden_anwenden (Auflage A10). Solange
  // er nicht geladen ist, wird KEIN Einstieg gezeigt (fail closed im UI; das
  // Tor lehnt einen Store-Zahler ohnehin serverseitig ab).
  const [payChannel, setPayChannel] = useState<string | null | undefined>(undefined)
  // Der Betrag kommt zur Laufzeit aus modul_preise — der EINEN gepflegten
  // Zahl (AK11/AK12). NULL = kein Betrag festgelegt; dann wird nicht bestellt.
  const [preis, setPreis] = useState<number | null>(null)
  const [offen, setOffen] = useState(false)
  const [kostenOk, setKostenOk] = useState(false)
  const [nummerOk, setNummerOk] = useState(false)
  const [busy, setBusy] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  useEffect(() => {
    if (!supabase) return
    void supabase.from('subscriptions').select('pay_channel')
      .eq('tenant_id', store.db.tenant.id).maybeSingle()
      .then(({ data, error }) => {
        if (!error) setPayChannel((data as { pay_channel?: string | null } | null)?.pay_channel ?? null)
      })
  }, [store.db.tenant.id])

  useEffect(() => {
    if (!supabase) return
    void supabase.from('modul_preise').select('betrag').eq('code', 'rksv').maybeSingle()
      .then(({ data }) => {
        const b = (data as { betrag?: number | string | null } | null)?.betrag
        setPreis(b == null ? null : Number(b))
      })
    // `offen` in den Abhängigkeiten: jedes Aufklappen lädt den Betrag neu —
    // ein SQL-`update` erscheint ohne Build und ohne Deploy (AK11, T25).
  }, [store.db.tenant.id, offen])

  const modul = status.modul
  const zustand = modul?.zustand ?? null
  // Nur AT (AK2): die Karte selbst erscheint für Nicht-AT nur im Altbestands-
  // Fall (Kasse bereits aktiviert) — auch dann gibt es hier keinen Einstieg.
  const bestellbar = store.db.tenant.country === 'AT'
    && (zustand === null || zustand === 'storniert' || zustand === 'beendet')
  const webZahler = payChannel !== undefined && (payChannel ?? 'stripe') === 'stripe'

  const uid = (store.db.tenant.vat_id ?? '').trim()
  const steuernummer = (store.db.tenant.tax_number ?? '').trim()
  // Angezeigt und bestätigt wird die Nummer in Anzeigeform — der Server
  // vergleicht normalisiert (T4b).
  const anzeigeNummer = uid !== '' ? uid : steuernummer !== '' ? formatieren(steuernummer) : null

  const preisText = preis == null ? null : formatMoney(preis)
  // Die Punkte a–f der Kostenübersicht (AK3) plus der Zuschlagsbeginn (F3).
  // Aus DENSELBEN Strings entstehen Anzeige und Bestellnachweis-Wortlaut —
  // was der Kunde bestätigt, ist wörtlich, was er gesehen hat (AK4).
  const punkte = preisText == null ? [] : [
    t('rksv.kosten.preis', { preis: preisText }),
    t('rksv.kosten.pflichten'),
    t('rksv.kosten.support'),
    t('rksv.kosten.supportWeg'),
    t('rksv.kosten.meldepflicht'),
    t('rksv.kosten.mindestbezug'),
    t('rksv.kosten.beginn'),
  ]
  const nummerZeile = anzeigeNummer == null ? '' : t('rksv.bestellung.nummer', { nummer: anzeigeNummer })
  const wortlaut = [t('rksv.kosten.titel'), ...punkte, nummerZeile].join('\n')

  const bestellen = async () => {
    if (anzeigeNummer == null || preis == null) return
    setBusy(true); setFehler(null)
    try {
      await call('bestellen', {
        bestaetigung: {
          text: wortlaut,
          sha256: await sha256Hex(wortlaut),
          nummer: anzeigeNummer,
          sprache: lang,
        },
      })
      setOffen(false); setKostenOk(false); setNummerOk(false)
      await reload()
    } catch (e) {
      const code = await rksvFehlerCode(e)
      setFehler(code === 'cost_summary_stale'
        ? t('rksv.bestellung.veraltet')
        : (code ?? String((e as Error).message)))
    }
    setBusy(false)
  }

  const stornieren = async () => {
    if (!window.confirm(t('rksv.bestellung.stornierenFrage'))) return
    setBusy(true); setFehler(null)
    try { await call('stornieren'); await reload() }
    catch (e) { setFehler((await rksvFehlerCode(e)) ?? String((e as Error).message)) }
    setBusy(false)
  }

  const kuendigen = async () => {
    if (!window.confirm(t('rksv.bestellung.kuendigenFrage'))) return
    setBusy(true); setFehler(null)
    try { await call('kuendigen'); await reload() }
    catch (e) {
      const code = await rksvFehlerCode(e)
      setFehler(code === 'minimum_term_not_reached' && modul?.mindestbezug_bis
        ? t('rksv.bestellung.kuendigenAb', { datum: formatDate(modul.mindestbezug_bis) })
        : (code ?? String((e as Error).message)))
    }
    setBusy(false)
  }

  return (
    <div className="rounded-xl bg-surface-2 p-3 space-y-2 text-sm">
      <p className="font-medium">{t('rksv.modul.titel')}</p>

      {zustand === 'bestellt' && (
        <>
          <p>{t('rksv.modul.bestellt')}</p>
          {/* AK13: die Zusage ist ehrlich — vor der Rahmenvertrags-Unterschrift
              (Gate off, aus `status` nach Auflage A4) ein Termin, danach die
              Werktage-Zusage (F5, Vorschlag 2). */}
          <p className="text-muted">
            {status.produktiv ? t('rksv.modul.zusage') : t('rksv.modul.termin')}
          </p>
          {istOwner && kaufwegSichtbar() && (
            <Button size="sm" variant="secondary" disabled={busy} onClick={stornieren}>
              {t('rksv.bestellung.stornieren')}
            </Button>
          )}
        </>
      )}

      {zustand === 'aktiv' && modul && (
        <>
          {modul.aktiv_ab && <p>{t('rksv.modul.aktivAb', { datum: formatDate(modul.aktiv_ab) })}</p>}
          <p className="text-muted">
            {modul.zuschlag_ab && t('rksv.modul.zuschlagAb', { datum: formatDate(modul.zuschlag_ab) })}
            {modul.zuschlag_ab && modul.mindestbezug_bis && ' · '}
            {modul.mindestbezug_bis && t('rksv.modul.mindestbezugBis', { datum: formatDate(modul.mindestbezug_bis) })}
            {/* Der Preis nur, wo ein Kaufweg gezeigt werden darf (AK14). */}
            {kaufwegSichtbar() && status.modul_preis != null
              && ` · ${t('rksv.modul.preisMonat', { preis: formatMoney(status.modul_preis) })}`}
          </p>
          {/* Schnitt 5 (T45): die Pflichten, die nur der Kunde erfüllen kann,
              stehen am Modulstatus — nicht nur in der einmal bestätigten
              Kostenübersicht. Wer sie hier liest, liest sie, WÄHREND sie gelten. */}
          <div>
            <p className="font-medium">{t('rksv.pflichten.titel')}</p>
            <ul className="list-disc pl-5 space-y-1 text-muted">
              <li>{t('rksv.pflichten.finanzonline')}</li>
              <li>{t('rksv.pflichten.startbeleg')}</li>
              <li>{t('rksv.pflichten.jahresbeleg')}</li>
              <li>{t('rksv.pflichten.dep')}</li>
              <li>{t('rksv.pflichten.kuendigung')}</li>
              <li>{t('rksv.pflichten.support')}</li>
            </ul>
          </div>
          {istOwner && kaufwegSichtbar() && (
            <Button size="sm" variant="secondary" disabled={busy} onClick={kuendigen}>
              {t('rksv.bestellung.kuendigen')}
            </Button>
          )}
        </>
      )}

      {zustand === 'gekuendigt' && modul?.gekuendigt_zum && (
        <>
          <p>{t('rksv.modul.gekuendigtZum', { datum: formatDate(modul.gekuendigt_zum) })}</p>
          {/* AK17/T45: nach der Kündigung bleibt die FinanzOnline-Abmeldung
              Kundenpflicht — und muss genau hier stehen, wo der Kunde nachsieht. */}
          <p className="text-muted">{t('rksv.pflichten.abmeldung')}</p>
        </>
      )}
      {zustand === 'storniert' && <p className="text-muted">{t('rksv.modul.storniert')}</p>}

      {bestellbar && kaufwegSichtbar() && (
        !istOwner ? (
          // AK6-UI (T18): der Zustand ist sichtbar, die Bestellung nicht auslösbar.
          <p className="text-xs text-muted">{t('rksv.bestellung.nurOwner')}</p>
        ) : payChannel === undefined ? null : !webZahler ? (
          // F2/AK14, ehrliche Meldung — das Tor lehnt zusätzlich serverseitig ab.
          <p className="text-xs text-muted">{t('rksv.bestellung.nurWeb')}</p>
        ) : !offen ? (
          <>
            <p className="text-muted">
              {t('rksv.modul.beschreibung')}
              {preisText && ` ${t('rksv.modul.preisMonat', { preis: preisText })}`}
            </p>
            <Button size="sm" variant="secondary" onClick={() => { setFehler(null); setOffen(true) }}>
              {t('rksv.bestellung.einstieg')}
            </Button>
          </>
        ) : anzeigeNummer == null ? (
          // AK7: ohne UID/Steuernummer endet die Strecke am Stammdaten-Feld,
          // mit dem Grund (Zertifikatstausch nach Aufwand).
          <p className="text-xs text-warn">{t('rksv.bestellung.nummerFehlt')}</p>
        ) : (
          <div className="space-y-2">
            <p className="font-medium">{t('rksv.kosten.titel')}</p>
            <ul className="list-disc pl-5 space-y-1 text-muted">
              {punkte.map((p, i) => <li key={i}>{p}</li>)}
            </ul>
            <p>{nummerZeile}</p>
            {/* Nicht vorangehakt, kein Weiterklicken ohne Haken (AK3/T16);
                die Nummer wird im selben Schritt bestätigt (AK9). */}
            <label className="flex items-start gap-2">
              <input type="checkbox" className="mt-0.5" checked={nummerOk}
                onChange={(e) => setNummerOk(e.target.checked)} />
              {t('rksv.bestellung.bestaetigenNummer')}
            </label>
            <label className="flex items-start gap-2">
              <input type="checkbox" className="mt-0.5" checked={kostenOk}
                onChange={(e) => setKostenOk(e.target.checked)} />
              {t('rksv.bestellung.bestaetigenKosten')}
            </label>
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" variant="primary"
                disabled={busy || !kostenOk || !nummerOk || preis == null}
                onClick={bestellen}>
                {t('rksv.bestellung.absenden')}
              </Button>
              <Button size="sm" variant="ghost" disabled={busy}
                onClick={() => { setOffen(false); setKostenOk(false); setNummerOk(false); setFehler(null) }}>
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        )
      )}

      {fehler && <p className="bg-danger-soft text-danger rounded-xl px-3 py-2">{fehler}</p>}
    </div>
  )
}

export function RksvCard() {
  const { lang, formatDate, formatMoney } = useI18n()
  const store = useStore()
  const de = lang === 'de'

  const [status, setStatus] = useState<RksvStatus | null>(null)
  const [limits, setLimits] = useState<RksvThresholds | null>(null)
  const [kassenId, setKassenId] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const call = useCallback(async (action: string, extra: Record<string, unknown> = {}) => {
    if (!supabase) throw new Error('no_backend')
    const { data, error: e } = await supabase.functions.invoke('rksv', { body: { action, ...extra } })
    if (e) throw e
    return data as Record<string, unknown>
  }, [])

  const load = useCallback(async () => {
    if (!store.live) return
    try {
      const s = await call('status') as unknown as RksvStatus
      setStatus(s)
      setKassenId(s.kassen_id || '')
      setLimits(await loadThresholds(store.db.tenant.id))
    } catch { /* card stays quiet */ }
  }, [store.live, call, store.db.tenant.id])

  useEffect(() => { void load() }, [load])

  if (!store.live) return null
  if (store.db.tenant.country !== 'AT' && !status?.enabled) return null

  const enable = async () => {
    setBusy(true); setError(null)
    // Kein `provider` im Rumpf: seit v19 setzt der Server ihn fest auf
    // fiskaltrust — mitgeschickt würde er ohnehin ignoriert (AK10, T12).
    try { await call('enable', { kassen_id: kassenId }); await load() }
    catch (e) { setError(String((e as Error).message)) }
    setBusy(false)
  }

  return (
    <Card className="p-4 space-y-3">
      <h2 className="font-medium flex items-center gap-2">
        <ShieldCheck size={18} /> {de ? 'Registrierkasse (RKSV)' : 'Cash register (RKSV)'}
      </h2>

      <p className="text-sm text-muted">
        {de
          ? 'Pflicht ab 15.000 € Jahresumsatz und 7.500 € Barumsatz — Kartenzahlungen zählen als Barumsatz. Jeder Beleg wird signiert, verkettet und im Datenerfassungsprotokoll abgelegt.'
          : 'Required above €15,000 turnover with €7,500 in cash — and card payments count as cash in Austria. Every receipt is signed, chained and kept in the audit log.'}
      </p>

      {limits && (
        <div className="rounded-xl bg-surface-2 p-3 space-y-2 text-sm">
          <div className="flex items-center gap-3">
            <span className="text-muted">{de ? 'Umsatz' : 'Turnover'} {limits.year}</span>
            <span className="ml-auto tabular-nums font-medium">
              {formatMoney(limits.turnover)} / {formatMoney(15000)}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-muted">
              {de ? 'davon Barumsatz' : 'of which cash-like'}
              <span className="block text-xs">
                {de ? 'Karte und Online zählen als bar' : 'card and online count as cash'}
              </span>
            </span>
            <span className="ml-auto tabular-nums font-medium">
              {formatMoney(limits.cash_like)} / {formatMoney(7500)}
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-border overflow-hidden">
            <div
              className={limits.obligated ? 'h-full bg-danger'
                : limits.closeness > 0.8 ? 'h-full bg-warn' : 'h-full bg-ok'}
              style={{ width: `${Math.min(100, Math.round(limits.closeness * 100))}%` }}
            />
          </div>
          {limits.obligated ? (
            <p className="text-danger">
              {de
                ? `Beide Grenzen überschritten am ${formatDate(limits.crossed_on!)}. Registrierkassenpflicht ab ${formatDate(limits.obliged_from!)} (bei quartalsweiser UVA).`
                : `Both limits passed on ${formatDate(limits.crossed_on!)}. A registered till is required from ${formatDate(limits.obliged_from!)} (quarterly VAT filing).`}
            </p>
          ) : limits.closeness > 0.8 ? (
            <p className="text-warn">
              {de
                ? 'Sie nähern sich der Registrierkassenpflicht. Sobald beide Grenzen überschritten sind, bleiben rund drei Monate Zeit.'
                : 'You are approaching the threshold. Once both limits are passed there are roughly three months to comply.'}
            </p>
          ) : (
            <p className="text-muted">
              {de ? 'Derzeit keine Registrierkassenpflicht.' : 'No registered till required at the moment.'}
            </p>
          )}
        </div>
      )}

      {status && !status.compliant && limits?.obligated && (
        <div className="text-sm bg-warn-soft text-warn rounded-xl px-3 py-2 space-y-1">
          <p className="font-medium flex items-center gap-2">
            <AlertTriangle size={15} /> {de ? 'Noch nicht rechtskonform' : 'Not compliant yet'}
          </p>
          <p>
            {de
              ? 'Es fehlt: der zertifizierte Signaturdienst (fiskaltrust), die Registrierung in FinanzOnline und der geprüfte Startbeleg. Bis dahin werden Belege protokolliert, aber nicht signiert.'
              : 'Missing: the certified signature service (fiskaltrust), FinanzOnline registration and a verified opening receipt. Until then receipts are logged but not signed.'}
          </p>
        </div>
      )}

      {/* Vorgang 018: Modulstatus + Bestellstrecke. Die Anbieterwahl, die hier
          bis Schnitt 5 stand, ist entfernt (F1/AK10, Renés Wort 18.08.2026):
          der Kunde sieht nur fiskaltrust, und der Server erzwingt es ohnehin
          seit v19 — die atrust/none-Pfade leben nur noch serverseitig weiter. */}
      {status && <RksvBestellstrecke status={status} call={call} reload={load} />}

      <div className="grid sm:grid-cols-2 gap-3">
        <Field label={de ? 'Kassenidentifikationsnummer' : 'Cash register id'}>
          <Input value={kassenId} onChange={(e) => setKassenId(e.target.value)} placeholder="KASSE-1" />
        </Field>
      </div>

      {error && <p className="text-sm bg-danger-soft text-danger rounded-xl px-3 py-2">{error}</p>}

      {status?.enabled && (
        <RksvGuide
          state={{
            enabled: status.enabled,
            credentialsSet: status.credentials_set === true,
            startbelegAt: status.startbeleg_at,
            finanzonlineAt: status.finanzonline_registered_at,
            receipts: status.receipts,
          }}
          busy={busy}
          onSaveCredentials={async (c) => {
            setBusy(true); setError(null)
            try { await call('credentials', c); await load() }
            catch (e) { setError(String((e as Error).message)) }
            setBusy(false)
          }}
          onRevealKey={async () => {
            try {
              const r = await call('reveal_key') as { aes_key?: string }
              return r.aes_key ?? null
            } catch (e) { setError(String((e as Error).message)); return null }
          }}
          onStartbeleg={async () => {
            setBusy(true); setError(null)
            try { await call('startbeleg'); await load() }
            catch (e) { setError(String((e as Error).message)) }
            setBusy(false)
          }}
          onFinanzOnline={async (done) => {
            setBusy(true); setError(null)
            try { await call('finanzonline_done', { done }); await load() }
            catch (e) { setError(String((e as Error).message)) }
            setBusy(false)
          }}
        />
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="primary" disabled={busy || !kassenId.trim()} onClick={enable}>
          {status?.enabled
            ? (de ? 'Einstellungen speichern' : 'Save settings')
            : (de ? 'Registrierkasse aktivieren' : 'Activate cash register')}
        </Button>
        {status?.enabled && (
          <span className="text-xs text-muted tabular-nums">
            {status.receipts} {de ? 'Belege im Protokoll' : 'receipts logged'}
          </span>
        )}
      </div>
    </Card>
  )
}
